import { useTranslation } from 'react-i18next'
import beforeMale from 'Assets/Images/before-after/m-before.webp'
import afterMale from 'Assets/Images/before-after/m-after.webp'
import beforeFemale from 'Assets/Images/before-after/f-before.webp'
import afterFemale from 'Assets/Images/before-after/f-after.webp'
import styled from 'styled-components'
import React from 'react'
import { ReinforcementScreenBackgrounds } from 'types/reinforcementScreenTypes'

const BeforeAfterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.mediumSmall};
  width: 100%;
`

const ResultContainer = styled.div<{ highlighted?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  // The gap is set to 12px to match the design since both 10px and 15px were either too small or too large
  gap: 12px;
  padding: 8px 8px 12px 8px;
  border-radius: ${({ theme }) => theme.spacing.mediumLarge};
  background-color: ${({ theme }) => theme.colors.grey50};
  color: ${({ theme }) => theme.colors.grey950};
  box-shadow: none;
  animation: ${({ highlighted }) => highlighted ? 'highlighted 1s ease-in-out' : 'none'};
  animation-delay: ${({ highlighted }) => highlighted ? '1s' : '0s'};
  max-width: 200px;
  animation-fill-mode: forwards;

  @keyframes highlighted {
    0% {
      box-shadow: 0px 108px 0px rgba(0, 0, 0, 0.00), 0px 69px 0px rgba(0, 0, 0, 0), 0px 39px 0px rgba(0, 0, 0, 0), 0px 17px 0px rgba(0, 0, 0, 0), 0px 4px 0px rgba(0, 0, 0, 0);
      color: ${({ theme }) => theme.colors.grey950};
      background-color: ${({ theme }) => theme.colors.grey50};
    }
    100% {
      box-shadow: 0px 108px 30px rgba(0, 0, 0, 0.00), 0px 69px 28px rgba(0, 0, 0, 0.01), 0px 39px 23px rgba(0, 0, 0, 0.05), 0px 17px 17px rgba(0, 0, 0, 0.09), 0px 4px 9px rgba(0, 0, 0, 0.10);
      color: ${({ theme }) => theme.colors.green700};
      background-color: ${({ theme }) => theme.colors.green100};
    }
  }
`

const ResultImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  object-position: top;
  border-radius: ${({ theme }) => theme.spacing.mediumSmall};
  overflow: hidden;
`

const ResultText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  text-align: center;
  margin: 0;
  font-weight: 600;
`

const comparisonImages: ReinforcementScreenBackgrounds<'before' | 'after'> = {
  m: {
    before: beforeMale,
    after: afterMale
  },
  f: {
    before: beforeFemale,
    after: afterFemale
  }
}

interface BeforeAfterProps {
  gender: IGender
}

const BeforeAfter: React.FC<BeforeAfterProps> = ({ gender }) => {
  const { t } = useTranslation()
  const genderKey = gender === 'm' ? 'm' : 'f'

  return (
    <BeforeAfterContainer>
      <ResultContainer>
        <ResultImage src={comparisonImages[genderKey]?.before} />
        <ResultText>{t('benefits.beforeAfter.labels.before')}</ResultText>
      </ResultContainer>
      <ResultContainer highlighted>
        <ResultImage src={comparisonImages[genderKey]?.after} />
        <ResultText>{t('benefits.beforeAfter.labels.after')}</ResultText>
      </ResultContainer>
    </BeforeAfterContainer>
  )
}

export default BeforeAfter
